const CAP19 = {
  id: 19,
  slug: "cap19-hashing-y-firmas-digitales-2",
  title: "Capítulo 19 — El hashing y las firmas digitales (III)",
  sections: [
    {
      heading: "Los elementos fundamentales del hashing",
      text: [
        "Las características del hashing —como ser irreversible, determinístico y resistente a colisiones— lo convierten en una herramienta poderosa para proteger datos e información.",
        "Estas propiedades lo hacen especialmente útil en **blockchain, firmas digitales, autenticidad de documentos y validación de integridad**.",
        "A continuación, exploramos los elementos esenciales que hacen posible este proceso."
      ]
    },
    {
      heading: "Algoritmos hash",
      text: [
        "Los algoritmos hash son **funciones matemáticas criptográficas** que transforman cualquier entrada (texto, archivo, foto, transacción) en un hash único de longitud fija.",
        "Actualmente existen diferentes algoritmos con varios niveles de seguridad. Algunos han dejado de usarse y otros son estándar en blockchain y seguridad digital."
      ],
      list: [
        "**MD5**: uno de los algoritmos más antiguos. Hoy ya NO es seguro debido a su vulnerabilidad a colisiones (distintas entradas producen el mismo hash).",
        "**SHA-1**: ampliamente utilizado, pero ha mostrado debilidades. No se recomienda para firmas digitales. El NIST recomendó su eliminación total para 2030.",
        "**SHA-2 (SHA-256, SHA-512)**: altamente seguros y usados ampliamente. **SHA-256 es el corazón de Bitcoin**, donde garantiza la seguridad e integridad de las transacciones.",
        "**SHA-3**: última adición de la familia SHA. Diseñado para resistir ataques de extensión y mejorar la seguridad en ciertos escenarios.",
        "**BLAKE2**: algoritmo avanzado, rápido y resistente a ataques criptográficos. Muy usado en firmas digitales y seguridad de archivos.",
        "**BLAKE3**: evolución de BLAKE2. Más rápido, seguro y eficiente. Ideal para protección de datos, autenticación y sistemas de archivos modernos."
      ]
    },
    {
      heading: "Código hash",
      text: [
        "El **código hash** es el resultado final del algoritmo de hashing. Es una cadena alfanumérica única, de longitud fija, que representa los datos originales.",
        "Por ejemplo, un archivo de texto de 5 KB o 5 GB producirá un hash de longitud fija (como 64 caracteres HEX en SHA-256).",
        "En blockchain, cada transacción genera un hash. Ese hash permite identificarla, verificar que no haya sido modificada y enlazarla dentro de un bloque, junto al hash del bloque anterior."
      ],
      list: [
        "🔹 El hash es único: representa exactamente los datos originales.",
        "🔹 Es inmutable: si los datos cambian, el hash cambia completamente.",
        "🔹 Es irreversible: no se puede reconstruir el archivo original a partir del hash.",
        "🔹 Permite detectar manipulación o alteración de datos al instante."
      ]
    },
    {
      heading: "Hashing y protección de documentos",
      text: [
        "El hashing es clave en las **firmas digitales**, ya que permite proteger documentos electrónicos.",
        "Antes de firmar, el documento se transforma en hash. Luego, ese hash se encripta con la clave privada del firmante.",
        "Si alguien modifica el documento, el hash cambiará, invalidando la firma."
      ],
      list: [
        "🔹 Permite verificar la integridad de contratos, diplomas, títulos, facturas, etc.",
        "🔹 Se usa en sistemas de autenticación, blockchain, certificados electrónicos y notarización digital.",
        "🔹 Hace posible la validación de documentos sin necesidad de compartir el contenido completo."
      ]
    },
    {
      heading: "El hashing y las firmas digitales",
      text: [
        "Una de las aplicaciones más importantes del hashing son las **firmas digitales criptográficas**, donde se combinan hashing y criptografía asimétrica.",
        "El hash del documento es firmado con una **clave privada**, generando una firma digital única.",
        "Luego, cualquier persona puede verificar la firma usando la **clave pública** del firmante y comparando el hash original."
      ],
      list: [
        "🔹 El documento se transforma en hash.",
        "🔹 El hash se encripta con la **clave privada** del firmante.",
        "🔹 Esto crea una **firma digital** única.",
        "🔹 El receptor desencripta la firma con la **clave pública**.",
        "🔹 Genera nuevamente el hash del documento.",
        "🔹 Si ambos hashes coinciden, el documento es auténtico y no ha sido modificado."
      ]
    },
    {
      heading: "¿Por qué hashing en las firmas digitales?",
      text: [
        "El hashing permite firmar documentos y transacciones de forma eficiente sin tener que cifrar la información completa (lo cual sería lento e ineficiente).",
        "Firmar el hash (y no el archivo completo) permite garantizar autenticidad, integridad y eficiencia."
      ],
      list: [
        "🔹 Asegura que el documento no se ha modificado desde la firma.",
        "🔹 Permite verificar la identidad del firmante con su clave pública.",
        "🔹 Reduce los costos computacionales (se firma solo el hash, no el documento entero)."
      ],
      note:
        "**En blockchain, cada transacción firmada sigue este exacto proceso: hashing + firma digital con clave privada.**"
    },
    {
      heading: "¿Qué veremos en el próximo capítulo?",
      text: [
        "En el siguiente capítulo profundizaremos aún más en las **firmas digitales (III)**, donde veremos:",
        "🔹 Cómo se crean y usan las claves pública y privada.",
        "🔹 Cómo las wallets firman transacciones sin revelar tu clave privada.",
        "🔹 Qué es ECDSA y por qué es el estándar en Ethereum y Bitcoin.",
        "🔹 Ejemplos visuales, analogías y demostraciones paso a paso."
      ]
    }
  ]
};

export default CAP19;
